import React from 'react';
import type {
  TaskAssignmentResponse,
  TaskDefinitionResponse,
} from 'src/api/ProactApiClient';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {toTaskDefinitionFormData} from 'src/app/tasks/taskFeatureTypes';
import {DocumentUnderReviewPanel} from 'src/app/tasks/DocumentUnderReviewPanel';
import {useFormSubmissionViewer} from 'src/app/forms/hooks/useFormSubmissionViewer';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Box} from 'src/design/atoms/Box';
import {Stack} from 'src/design/atoms/Stack';
import {useConnectivity} from 'src/offline/useConnectivity';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {taskFieldDefinitions} from 'src/app/tasks/taskHelpers';
import {useApprovalFormMeta} from './ApprovalForm';

export const ApprovalDefinitionView: React.FC<{
  item: TaskDefinitionResponse;
}> = ({item}) => {
  const {formMeta, isLoading, error} = useApprovalFormMeta(item.state);
  if (isLoading) return <TableBodyRender isLoading />;
  if (error) return <ErrorAlert message={parseErrorMessage(error)} />;
  const visibleMeta = formMeta.filter(field => field.name !== 'subject');
  const formData = toTaskDefinitionFormData(item);

  return (
    <Stack gap="md">
      {item.documentId && (
        <DocumentUnderReviewPanel
          documentId={item.documentId}
          documentVersion={item.documentVersion}
        />
      )}
      <FormViewComponentV2 formData={formData} formMeta={visibleMeta} />
    </Stack>
  );
};

const ApprovalSourceFormView: React.FC<{
  formDefinitionId: number;
  formSubmissionId: number;
  fallbackIdentifier?: string;
}> = ({formDefinitionId, formSubmissionId, fallbackIdentifier}) => {
  const {form, formDefinition, formData, formMeta, isLoading} =
    useFormSubmissionViewer({formDefinitionId, formSubmissionId});
  if (isLoading) return <TableBodyRender isLoading />;

  const identifier = form?.internalIdentifier ?? fallbackIdentifier;
  const base = formDefinition?.name ?? 'Source form';
  const title = identifier ? `${base} · ${identifier}` : base;

  return (
    <SectionCard title={title} data-testid="approval-source-form">
      <Box p="md">
        <FormViewComponentV2 formData={formData} formMeta={formMeta} />
      </Box>
    </SectionCard>
  );
};

export const ApprovalAssignmentView: React.FC<{
  item: TaskAssignmentResponse;
}> = ({item}) => {
  const {isOffline} = useConnectivity();
  const {sourceFormId, sourceFormDefinitionId, sourceFormIdentifier} =
    item.taskDefinition;
  const content = (
    <FormViewComponentV2
      formData={toFormDataV2(item.taskDefinition)}
      formMeta={[taskFieldDefinitions.content]}
    />
  );
  if (!sourceFormId || !sourceFormDefinitionId || isOffline) return content;

  return (
    <Stack gap="md">
      <ApprovalSourceFormView
        formDefinitionId={sourceFormDefinitionId}
        formSubmissionId={sourceFormId}
        fallbackIdentifier={sourceFormIdentifier}
      />
      {content}
    </Stack>
  );
};
